import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, FileText, Send, Clock } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  { v: "dienstverlening", label: "Dienstverlening / bemiddeling" },
  { v: "fotografie", label: "Fotografie service" },
  { v: "privacy", label: "Privacy / gegevensverwerking" },
  { v: "facturatie", label: "Facturatie / betaling" },
  { v: "communicatie", label: "Communicatie / bereikbaarheid" },
  { v: "anders", label: "Anders" },
];

export default function Complaints() {
  const [f, setF] = useState({ name: "", email: "", phone: "", category: "dienstverlening", subject: "", description: "", order_or_property_id: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.subject || !f.description) return toast.error("Vul alle verplichte velden in.");
    setBusy(true);
    try {
      const { data } = await http.post("/complaints", f);
      setDone(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="max-w-2xl mx-auto py-24 px-4 text-center">
      <CheckCircle2 className="w-16 h-16 text-[color:var(--wv-green)] mx-auto" />
      <h1 className="font-display text-4xl font-extrabold mt-4">Uw klacht is geregistreerd</h1>
      <p className="mt-3 text-slate-600">Bedankt voor uw feedback. Wij nemen dit serieus en reageren binnen 2 werkdagen.</p>
      <div className="mt-6 p-5 rounded-2xl bg-white border border-slate-200 inline-block">
        <p className="text-xs uppercase font-bold tracking-wider text-slate-500">Ticket</p>
        <p className="font-mono-num text-2xl font-extrabold mt-1" data-testid="complaint-ticket">{done.ticket}</p>
      </div>
      <p className="mt-6 text-sm text-slate-500">U ontvangt een bevestiging per e-mail. Bewaar het ticketnummer voor uw referentie.</p>
    </div>
  );

  return (
    <div>
      <section className="wv-hero-grain bg-gradient-to-b from-[#F8F9FA] to-white py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
          <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">Klachten</p>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--wv-obsidian)] mt-2" data-testid="complaints-title">Wij horen graag als iets niet goed ging.</h1>
          <p className="mt-3 text-slate-600 max-w-2xl">Een goede klachtenprocedure hoort bij een betrouwbaar platform. Vul dit formulier in — u krijgt een officieel ticketnummer en wij reageren binnen 2 werkdagen.</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-10 grid md:grid-cols-3 gap-4">
        {[
          { icon: Send, title: "1. Indienen", desc: "U vult het formulier in. Alle vragen zijn duidelijk en kort." },
          { icon: FileText, title: "2. Registratie", desc: "U ontvangt binnen enkele minuten een officieel ticketnummer per e-mail." },
          { icon: Clock, title: "3. Reactie", desc: "Wij reageren binnen 2 werkdagen. Streef naar oplossing binnen 14 dagen." },
        ].map((s, i) => (
          <div key={i} className="p-5 rounded-2xl bg-white border border-slate-200">
            <s.icon className="w-6 h-6 text-[color:var(--wv-green)]" />
            <p className="mt-3 font-display font-bold">{s.title}</p>
            <p className="mt-1 text-sm text-slate-600">{s.desc}</p>
          </div>
        ))}
      </section>

      <form onSubmit={submit} className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 pb-16">
        <div className="p-6 md:p-8 bg-white border border-slate-200 rounded-2xl grid md:grid-cols-2 gap-4">
          <div><label className="text-sm font-semibold">Naam *</label><input className="wv-input mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cmp-name" required /></div>
          <div><label className="text-sm font-semibold">Email *</label><input className="wv-input mt-1" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="cmp-email" required /></div>
          <div><label className="text-sm font-semibold">Telefoon (optioneel)</label><input className="wv-input mt-1" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="cmp-phone" /></div>
          <div>
            <label className="text-sm font-semibold">Categorie *</label>
            <select className="wv-input mt-1" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="cmp-category">
              {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2"><label className="text-sm font-semibold">Onderwerp *</label><input className="wv-input mt-1" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="cmp-subject" required /></div>
          <div className="md:col-span-2"><label className="text-sm font-semibold">Woning-ID of order (optioneel)</label><input className="wv-input mt-1" value={f.order_or_property_id} onChange={(e) => setF({ ...f, order_or_property_id: e.target.value })} data-testid="cmp-ref" /></div>
          <div className="md:col-span-2"><label className="text-sm font-semibold">Beschrijving *</label><textarea className="wv-input mt-1" rows="6" placeholder="Beschrijf zo concreet mogelijk wat er is gebeurd, wanneer, en wat u van ons verwacht." value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="cmp-description" required /></div>
        </div>

        <div className="mt-4 flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-900">Uw gegevens worden alleen gebruikt voor de afhandeling van deze klacht. Zie onze <Link to="/privacy" className="underline font-semibold">privacyverklaring</Link>.</p>
        </div>

        <div className="mt-6 flex justify-end">
          <button type="submit" disabled={busy} className="wv-btn-primary inline-flex items-center gap-2" data-testid="cmp-submit">
            <Send className="w-4 h-4" /> {busy ? "Verzenden…" : "Klacht indienen"}
          </button>
        </div>
      </form>
    </div>
  );
}
